import { sdk } from "@/lib/api/sdk-instance.js";

const mapListResponse = (response, defaults = {}) => {
  const pagination = response?.pagination || {};
  const meta = response?.meta || {};
  const page = response?.page ?? pagination.page ?? meta.page ?? defaults.page ?? 1;
  const pageSize = response?.pageSize ?? pagination.pageSize ?? meta.pageSize ?? defaults.pageSize ?? 10;
  const total = response?.total ?? pagination.total ?? meta.total ?? 0;
  const totalPages =
    response?.totalPages ??
    pagination.totalPages ??
    meta.totalPages ??
    (Math.ceil(total / pageSize) || 0);

  const out = {
    data: response?.data || [],
    page,
    pageSize,
    total,
    totalPages,
    uniqueValues: response?.uniqueValues || {},
    summary: response?.summary || {},
    hasNextPage: pagination.hasNextPage ?? meta.hasNextPage ?? page < totalPages,
    hasPreviousPage: pagination.hasPreviousPage ?? meta.hasPreviousPage ?? page > 1,
  };
  return out;
};

export const controlApi = {
  getAll: async (params = {}) => {
    const query = {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    };

    if (params.excludeIds != null && params.excludeIds !== "") {
      const arr = Array.isArray(params.excludeIds)
        ? params.excludeIds
        : String(params.excludeIds)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
      if (arr.length > 0) {
        query.excludeIds = arr.join(",");
      }
    }

    const columnFilters = params.columnFilters || {};
    Object.entries(columnFilters).forEach(([key, value]) => {
      if (value && value.length > 0) {
        query[`filter_${key}`] = value;
      }
    });
    if (Array.isArray(params.scopeStandards) && params.scopeStandards.length > 0) {
      query.filter_scope_standards = params.scopeStandards;
    }
    if (Array.isArray(params.categoryDomains) && params.categoryDomains.length > 0) {
      query.filter_category_domains = params.categoryDomains;
    }
    if (Array.isArray(params.catalogSeedTagsFilter) && params.catalogSeedTagsFilter.length > 0) {
      query.filter_catalog_seed_tags = params.catalogSeedTagsFilter;
    }
    const response = await sdk.control.getAll(query);
    return mapListResponse(response, params);
  },

  getScopeOptions: async () => {
    const res = await sdk.control.getScopeOptions();
    return Array.isArray(res?.data) ? res.data : [];
  },

  getCategoryOptions: async () => {
    const res = await sdk.control.getCategoryOptions();
    return Array.isArray(res?.data) ? res.data : [];
  },

  getScopePreference: async () => {
    const res = await sdk.control.getScopePreference();
    return res?.data || {
      scope_standards: [],
      category_domains: [],
      catalog_seed_tags_filter: [],
    };
  },

  getById: async (id) => {
    const res = await sdk.control.getById(id);
    return res.data;
  },

  getStandardsMapping: async (id, params = {}) => {
    const query = {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    };
    const res = await sdk.control.getStandardsMapping(id, query);
    return mapListResponse(
      {
        data: res.data,
        pagination: res.meta,
        summary: res.summary,
      },
      params
    );
  },

  getCurrentEvaluation: async (id) => {
    const res = await sdk.control.getCurrentEvaluation(id);
    return res.data;
  },

  createEvaluationVersion: async (id, body) => {
    const res = await sdk.control.createEvaluationVersion(id, body);
    return res.data;
  },

  uploadEvaluationEvidence: async (id, file, onUploadProgress) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await sdk.control.uploadEvaluationEvidence(id, formData, { onUploadProgress });
    return res?.data;
  },

  downloadEvaluationEvidence: async (id, storedName) => {
    return sdk.control.downloadEvaluationEvidence(id, storedName);
  },

  deleteEvaluationEvidence: async (id, storedName) => {
    const res = await sdk.control.deleteEvaluationEvidence(id, storedName);
    return res?.data;
  },

  getEvaluationHistory: async (id, params = {}) => {
    const query = {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    };
    const columnFilters = params.columnFilters || {};
    Object.entries(columnFilters).forEach(([key, value]) => {
      if (value && value.length > 0) {
        query[`filter_${key}`] = value;
      }
    });
    const res = await sdk.control.getEvaluationHistory(id, query);
    return mapListResponse(
      {
        data: res.data,
        pagination: res.meta,
        uniqueValues: res.uniqueValues,
      },
      params
    );
  },

  create: async (data) => {
    const res = await sdk.control.create(data);
    return res.data;
  },

  update: async (id, data) => {
    const res = await sdk.control.update(id, data);
    return res.data;
  },

  delete: async (id) => {
    const res = await sdk.control.delete(id);
    return res.data;
  },

  search: async (query) => {
    const res = await sdk.control.getAll({ search: query });
    return res.data || [];
  },

  getRelatedAssessmentQuestions: async (id, params = {}) => {
    const query = {
      page: params.page,
      pageSize: params.pageSize,
      limit: params.limit,
      search: params.search,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    };
    const res = await sdk.control.getRelatedAssessmentQuestions(id, query);
    const mapped = mapListResponse(
      {
        data: res.data,
        pagination: res.meta || res.pagination,
      },
      params
    );
    mapped.linkedQuestionIds = res.linkedQuestionIds || [];
    return mapped;
  },

  linkAssessmentQuestions: async (id, questionIds) => {
    const res = await sdk.control.linkAssessmentQuestions(id, { questionIds });
    return res.data;
  },

  unlinkAssessmentQuestion: async (id, questionId) => {
    const res = await sdk.control.unlinkAssessmentQuestion(id, questionId);
    return res.data;
  },

  bulkUnlinkAssessmentQuestions: async (id, questionIds) => {
    const res = await sdk.control.bulkUnlinkAssessmentQuestions(id, { questionIds });
    return res.data;
  },

  deleteRelatedAssessmentQuestion: async (id, questionId) => {
    const res = await sdk.control.deleteRelatedAssessmentQuestion(id, questionId);
    return res.data;
  },

  bulkDeleteRelatedAssessmentQuestions: async (id, questionIds) => {
    const res = await sdk.control.bulkDeleteRelatedAssessmentQuestions(id, { questionIds });
    return res.data;
  },

  getSoaList: async (params = {}) => {
    const query = {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
    };
    const columnFilters = params.columnFilters || {};
    Object.entries(columnFilters).forEach(([key, value]) => {
      if (value && value.length > 0) {
        query[`filter_${key}`] = value;
      }
    });
    const res = await sdk.control.getSoaList(query);
    const pagination = res.meta || {};
    return {
      data: res.data || [],
      summary: res.summary || {},
      page: pagination.page ?? params.page ?? 1,
      pageSize: pagination.pageSize ?? params.pageSize ?? 25,
      total: pagination.total ?? 0,
      totalPages: pagination.totalPages ?? 0,
      hasNextPage: pagination.hasNextPage,
      hasPreviousPage: pagination.hasPreviousPage,
      uniqueValues: res.uniqueValues || {},
    };
  },

  getRelatedDocuments: async (id, params = {}) => {
    const query = {
      page: params.page,
      pageSize: params.pageSize || params.limit,
      search: params.search,
      sortBy: params.sortBy || "documentName",
      sortOrder: params.sortOrder || params.sortDirection || "asc",
    };
    const res = await sdk.control.getRelatedDocuments(id, query);
    const mapped = mapListResponse(
      {
        data: res.data,
        pagination: res.meta || res.pagination,
      },
      params
    );
    mapped.linkedDocumentIds = res.linkedDocumentIds || [];
    return mapped;
  },

  linkDocuments: async (id, documentIds) => {
    const res = await sdk.control.linkDocuments(id, { documentIds });
    return res.data;
  },

  unlinkDocument: async (id, documentId) => {
    const res = await sdk.control.unlinkDocument(id, documentId);
    return res.data;
  },

  bulkUnlinkDocuments: async (id, documentIds) => {
    const res = await sdk.control.bulkUnlinkDocuments(id, { documentIds });
    return res.data;
  },
};

